import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useTenantStore } from './useTenantStore';
import { useServices } from '../api/services';
import { useResetUserScopedStores } from '../composables/useResetUserScopedStores';
import { isUnauthorizedError } from '../utils/isUnauthorizedError';
import { safeStorage } from '../utils/safeStorage';
import { setSentryUser, clearSentryUser } from '../sentry/context';
import {
  setDatadogRumUser,
  clearDatadogRumUser,
} from '../datadog/context';
import type { Customer } from '../types/customer';

export type { Customer } from '../types/customer';

function identifyPendo(u: { id: number | string; email?: string }): void {
  if (
    typeof window === 'undefined' ||
    typeof window.pendo === 'undefined' ||
    typeof window.pendo.initialize !== 'function'
  ) {
    return;
  }
  const tenantStore = useTenantStore();
  const tenantSlug = tenantStore.config?.tenantSlug ?? 'unknown';
  window.pendo.initialize({
    visitor: { id: u.id },
    account: { id: tenantSlug },
  });
}

// quick-260629-lm6 — mirror identifyPendo's shape for Sentry user context.
// Maps a full Customer OR the partial localStorage user ({ id, role, email })
// into a SentryUser. `username` is computed from first/last name and passed
// ONLY when non-empty, so the partial rehydrate (no first/last) yields no
// username key. Imports the helper from ../sentry/context (NOT the Sentry SDK)
// to keep the leaf-level store free of the SDK (dependency direction).
function identifySentry(u: {
  id: number | string;
  email: string;
  role?: string;
  first_name?: string;
  last_name?: string;
}): void {
  const username = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  setSentryUser({
    id: u.id,
    email: u.email,
    role: u.role,
    ...(username ? { username } : {}),
  });
}

// Mirror identifySentry for Datadog RUM user context. Same mapping and same
// dependency-direction reasoning: imports the helper from ../datadog/context
// (NOT the RUM SDK) so this leaf-level store stays free of the SDK. Safe when
// RUM is disabled — the SDK buffers/no-ops on an uninitialized instance.
function identifyDatadog(u: {
  id: number | string;
  email: string;
  role?: string;
  first_name?: string;
  last_name?: string;
}): void {
  const username = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  setDatadogRumUser({
    id: u.id,
    email: u.email,
    role: u.role,
    ...(username ? { username } : {}),
  });
}

function readStoredSession(): {
  token: string;
  user: Pick<Customer, 'id' | 'role' | 'email'>;
} | null {
  try {
    const stored = safeStorage.getItem('authToken');
    const userStr = safeStorage.getItem('authUser');
    if (!stored || !userStr) return null;
    const parsedUser = JSON.parse(userStr) as Pick<
      Customer,
      'id' | 'role' | 'email'
    >;
    return { token: stored, user: parsedUser };
  } catch {
    return null;
  }
}

// eslint-disable-next-line max-lines-per-function -- defineStore setup function aggregates all auth actions; splitting into separate composables would be a structural refactor outside this fix's scope.
export const useAuthStore = defineStore('auth', () => {
  const _stored = readStoredSession();
  const user = ref<Customer | null>(
    _stored
      ? ({
          ..._stored.user,
          authentication_token: _stored.token,
          cable_token: '',
          addresses: [],
        } as unknown as Customer)
      : null,
  );
  const token = ref<string | null>(_stored?.token ?? null);
  const isAuthenticated = computed(
    () => user.value !== null && token.value !== null,
  );

  async function login(email: string, password: string): Promise<void> {
    const { auth } = useServices();
    const data = await auth.login(email, password);
    user.value = data;
    token.value = data.authentication_token;
    safeStorage.setItem('authToken', data.authentication_token);
    safeStorage.setItem(
      'authUser',
      JSON.stringify({ id: data.id, role: data.role, email: data.email }),
    );
    safeStorage.setItem('lastEmail', data.email);
    identifyPendo(data);
    identifySentry(data);
    identifyDatadog(data);

    // sign_in's CustomerSerializer returns a leaner customer than find_by_email's
    // show.json.jbuilder — it omits phones, primary_address, memberships /
    // membership_status, and accounting_key. restoreSession() (the boot path)
    // hydrates via findByEmail, so without this an in-session re-login leaves the
    // customer object less complete than a fresh boot (quick-260624-d4x). Persist
    // the session FIRST (above) so the apiClient interceptor sends the fresh token
    // on this request, then re-hydrate to make both paths produce an identical
    // customer. Failure-safe by design (mirrors refreshPaymentMethod): a transient
    // findByEmail failure must NEVER break a just-succeeded login — keep the
    // sign_in payload already assigned above.
    try {
      const full = await auth.findByEmail(data.email);
      if (full) user.value = full;
    } catch {
      // keep the sign_in payload (user.value = data, set above)
    }
  }

  async function logout(): Promise<void> {
    try {
      await useServices().auth.signOut();
    } catch {
      // Backend teardown failed (network or HTTP error) — proceed with
      // local cleanup so the user is not stranded in an auth limbo.
    }
    user.value = null;
    token.value = null;
    safeStorage.removeItem('authToken');
    safeStorage.removeItem('authUser');
    // quick-260527-njp: clear user-scoped Pinia stores so a subsequent login as
    // a different account can't briefly render the prior account's data.
    // Resolved lazily here (not at store-setup) to avoid Pinia
    // store-resolution-order issues, matching the appointments store's lazy
    // useInvoicesStore() pattern.
    useResetUserScopedStores().resetAll();
    // quick-260629-lm6 — detach the Sentry user + tenant tag on logout so a
    // subsequent error isn't mis-attributed to the prior customer. Mirrors
    // identifyPendo parity: clearSentryUser is NOT added to handleUnauthorized
    // because identifyPendo isn't called there either (intentional asymmetry).
    clearSentryUser();
    clearDatadogRumUser();
  }

  async function restoreSession(): Promise<void> {
    const stored = safeStorage.getItem('authToken');
    if (!stored) return;

    const userStr = safeStorage.getItem('authUser');
    if (!userStr) {
      safeStorage.removeItem('authToken');
      return;
    }

    let parsedUser: Pick<Customer, 'id' | 'role' | 'email'>;
    try {
      parsedUser = JSON.parse(userStr) as Pick<
        Customer,
        'id' | 'role' | 'email'
      >;
    } catch {
      safeStorage.removeItem('authToken');
      safeStorage.removeItem('authUser');
      return;
    }

    token.value = stored;

    try {
      const { auth } = useServices();
      const data = await auth.findByEmail(parsedUser.email);
      user.value = data;
      identifyPendo(data);
      identifySentry(data);
      identifyDatadog(data);
    } catch (err) {
      // 401 means the stored token is verifiably invalid. Clear it locally
      // rather than dispatching auth:session-expired (the interceptor's
      // findByEmail allowlist suppresses that) — public routes like
      // /schedule-appointment must continue to load with no session.
      if (isUnauthorizedError(err)) {
        user.value = null;
        token.value = null;
        safeStorage.removeItem('authToken');
        safeStorage.removeItem('authUser');
        return;
      }
      // Non-401 (network, 5xx) is treated as transient: keep the cached user
      // so the app stays usable offline / on flaky APIs.
      user.value = {
        ...parsedUser,
        authentication_token: stored,
        cable_token: '',
        addresses: [],
      } as unknown as Customer;
      identifyPendo(parsedUser);
      // Partial localStorage user has no first/last → no username key sent.
      identifySentry(parsedUser);
      identifyDatadog(parsedUser);
    }
  }

  async function handleUnauthorized(): Promise<void> {
    const { pathname } = window.location;
    // Idempotency guard (PODZ-1704): once we're on /login, a prior 401's
    // listener already started the bounce. Skip subsequent dispatches —
    // re-running would either no-op (router.replace to the same place) or
    // nest a new ?redirect= around the existing one when multiple in-flight
    // requests (e.g. the wizard's 4 parallel availability fetches) all
    // 401 in the same tick.
    if (pathname === '/login' || pathname === '/login/') return;
    const path = pathname + window.location.search;
    const redirect = path;
    user.value = null;
    token.value = null;
    safeStorage.removeItem('authToken');
    safeStorage.removeItem('authUser');
    // quick-260527-njp: session expiry must also clear user-scoped stores so
    // stale prior-account data isn't left on screen mid-redirect. Done before
    // dispatching the session-expired event. Resolved lazily (see logout()).
    useResetUserScopedStores().resetAll();
    window.dispatchEvent(
      new CustomEvent('auth:session-expired', {
        detail: { redirect: encodeURIComponent(redirect) },
      }),
    );
  }

  // Called by PayModal after a successful payment-method delete so the on-file
  // method is dropped locally without re-fetching the customer: `hasSavedMethod`
  // recomputes false and the pay modal falls through to new-method entry. Null
  // user is a safe no-op (the synchronous boot before restoreSession can have
  // no user yet).
  function clearPaymentMethod(): void {
    if (user.value) user.value.payment_method = null;
  }

  // Re-fetch ONLY the on-file payment method after a save-on-charge, so the Pay
  // Modal's saved-method view reflects a just-saved method WITHOUT a hard refresh
  // (the modal reads auth.user.payment_method, which otherwise only repopulates
  // via findByEmail on restoreSession). Targeted + failure-safe by design: unlike
  // restoreSession, a transient findByEmail failure here must NEVER degrade the
  // session — we keep the current payment_method (the saved method still appears
  // on the next hard refresh). Works from EVERY modal entry point (appt detail,
  // invoice list, invoice detail) because they all read this one shared field.
  async function refreshPaymentMethod(): Promise<void> {
    if (!user.value) return;
    try {
      const { auth } = useServices();
      const data = await auth.findByEmail(user.value.email);
      if (user.value) {
        user.value.payment_method = data.payment_method ?? null;
        // PODZ-1817: keep the server-minted create URL in lockstep with the
        // method. The server nulls payment_method_create_url whenever a method
        // is on file (customer_serializer.rb:66); after a save/remove this URL
        // MUST reflect the new state so the Add/Update modal can POST again.
        user.value.payment_method_create_url =
          data.payment_method_create_url ?? null;
      }
    } catch {
      // Non-fatal: leave the existing payment_method intact.
    }
  }

  // PODZ-1817: re-fetch the FULL customer (NOT just payment_method) so EVERY
  // server-minted field — notably payment_method_create_url, which the server
  // nulls once a method is on file — reflects current state. Used by the
  // Add/Update payment-method modal's remove-then-add update path: after the
  // existing method is deleted, the server re-mints the create URL, and this
  // pulls it into `user.value` so the subsequent POST has a target. Preserves
  // the session token (assigns user.value only, like setUser — NEVER touches
  // localStorage/token; threat T-06.2-04). Failure-safe: a transient error
  // leaves the session intact and rethrows so callers can surface a retry.
  async function refreshUser(): Promise<void> {
    if (!user.value) return;
    const { auth } = useServices();
    const data = await auth.findByEmail(user.value.email);
    // Keep the live session token (findByEmail's token is not the session one).
    const sessionToken = user.value.authentication_token;
    user.value = { ...data, authentication_token: sessionToken };
  }

  // Phase 6.2 (D-05) — sink for profile mutations. `updateContact` returns the
  // full customer; address removal (quick 260622-itv) passes a locally-rebuilt
  // customer with the deleted row dropped (DELETE renders no customer). Assigns
  // `user.value` ONLY: unlike setSession it does NOT re-persist the localStorage
  // token (authToken / authUser / lastEmail) and does NOT call identifyPendo.
  // Profile edits must never touch the token lifecycle (V3 session note / threat
  // T-06.2-04).
  function setUser(customer: Customer): void {
    user.value = customer;
  }

  function setSession(customer: Customer): void {
    user.value = customer;
    token.value = customer.authentication_token;
    safeStorage.setItem('authToken', customer.authentication_token);
    safeStorage.setItem(
      'authUser',
      JSON.stringify({
        id: customer.id,
        role: customer.role,
        email: customer.email,
      }),
    );
    safeStorage.setItem('lastEmail', customer.email);
    identifyPendo(customer);
    identifySentry(customer);
    identifyDatadog(customer);
  }

  return {
    user,
    token,
    isAuthenticated,
    login,
    logout,
    restoreSession,
    handleUnauthorized,
    setSession,
    setUser,
    clearPaymentMethod,
    refreshPaymentMethod,
    refreshUser,
  };
});
